import { useQuery } from '@tanstack/react-query';
import { Users, TrendingUp, UserPlus, Clock, CreditCard } from 'lucide-react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { PageLoader } from '../components/ui/Spinner';
import { apiFetch } from '../lib/apiFetch';
import { formatUzs, formatNumber, formatDate } from '../lib/format';
import { useT } from '../i18n';

interface DashboardStats {
  active_students: number;
  revenue_uzs: string;
  new_signups: number;
  expiring_7d: number;
  pending_payments: number;
}

interface Point {
  date: string;
  revenue_uzs?: string;
  signups?: number;
}

export function Dashboard() {
  const t = useT();
  const stats = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => apiFetch<DashboardStats>('/api/admin/dashboard/stats', { query: { period: 30 } }),
  });
  const revenue = useQuery({
    queryKey: ['dashboard', 'revenue'],
    queryFn: () =>
      apiFetch<{ points: Point[] }>('/api/admin/dashboard/revenue', { query: { period: 30 } }),
  });
  const signups = useQuery({
    queryKey: ['dashboard', 'signups'],
    queryFn: () =>
      apiFetch<{ points: Point[] }>('/api/admin/dashboard/signups', { query: { period: 30 } }),
  });

  if (stats.isLoading) return <PageLoader />;
  if (!stats.data) return <p className="text-slate-500">{t('common.noData')}</p>;
  const s = stats.data;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label={t('dashboard.activeStudents')} value={formatNumber(s.active_students)} Icon={Users} accent="emerald" />
        <Kpi label={t('dashboard.revenue')} value={formatUzs(s.revenue_uzs)} Icon={TrendingUp} accent="emerald" />
        <Kpi label={t('dashboard.signups')} value={formatNumber(s.new_signups)} Icon={UserPlus} accent="blue" />
        <Kpi label={t('dashboard.expiring7d')} value={formatNumber(s.expiring_7d)} Icon={Clock} accent="amber" />
        <Kpi label={t('dashboard.pendingPayments')} value={formatNumber(s.pending_payments)} Icon={CreditCard} accent={s.pending_payments > 0 ? 'red' : 'slate'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.revenueByDay')}</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {revenue.data ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenue.data.points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={(v) => formatDate(v)} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip
                    labelFormatter={(v) => formatDate(v as string)}
                    formatter={(value) => [formatUzs(value as string | number), t('dashboard.revenueLabel')]}
                  />
                  <Line type="monotone" dataKey="revenue_uzs" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.signupsByDay')}</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {signups.data ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={signups.data.points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tickFormatter={(v) => formatDate(v)} fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(v) => formatDate(v as string)}
                    formatter={(value) => [value, t('dashboard.signupsLabel')]}
                  />
                  <Bar dataKey="signups" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const ACCENTS: Record<string, string> = {
  emerald: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-blue-50 text-blue-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  slate: 'bg-slate-50 text-slate-700',
};

function Kpi({
  label,
  value,
  Icon,
  accent = 'slate',
}: {
  label: string;
  value: string;
  Icon: typeof Users;
  accent?: keyof typeof ACCENTS;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${ACCENTS[accent]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-lg font-semibold text-slate-900">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
